"use client";

import { useState, useEffect, useRef } from "react";
import { useClientReady } from "@/hooks/useClientReady";
import { getZodiacSign } from "@/lib/zodiac";
import { createPortal } from "react-dom";
import { LoveMapResponse, TarotRequestCreate } from "@/types/tarot";
import { TarotApiService } from "@/services/tarotApi";
import { pricingService, PricingConfig } from "@/services/pricing";
import {
  trackStep,
  trackInitiateCheckout,
} from "@/services/analytics";
import { useRouter } from "next/navigation";
import {
  config,
  getMarketPhoneCountry,
  isPortugueseLanguage,
} from "@/config/app";
import { validatePhoneForWhatsApp } from "@/lib/phoneUtils";
import { Step1Contact } from "@/components/steps/Step1Contact";
import { Step2Querent } from "@/components/steps/Step2Querent";
import { Step3ChanneledPerson } from "@/components/steps/Step3ChanneledPerson";
// import { Step6Changes } from "@/components/steps/Step6Changes"; // REMOVED - Step 6 is now hidden
import { Step7Preferences } from "@/components/steps/Step7Preferences";
import { Step7DoubtsDialog } from "@/components/steps/Step7DoubtsDialog";
import { ErrorLogService, ErrorSeverity } from "@/services/errorLog";
import { Step8Payment } from "@/components/steps/Step8Payment";
import {
  PaymentSuccessScreen,
  type SuccessLoveMap,
} from "@/components/PaymentSuccessScreen";
import { toSuccessLoveMap } from "@/lib/loveMap";
import { useI18n } from "@/i18n/I18nProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { detectLikelyEmailDomainTypo } from "@/lib/emailDomainSuggestion";
import { ContactHistory, emptyContactHistory, readContactHistory, rememberContacts } from "@/lib/contactHistory";
import { PersonalHistory, emptyPersonalHistory, readPersonalHistory, rememberPersonalDetails } from "@/lib/personalHistory";

// Function to generate initial form data based on language
function getInitialFormData(language: string): TarotRequestCreate {
  // WhatsApp is available in Portuguese markets and is the preferred default.
  const defaultDeliveryOption =
    language === "en-US" ? "email" : "whatsapp";

  return {
    delivery_option: defaultDeliveryOption,
    querent_phone: "",
    querent_phone_confirmation: "", // Campo adicional para confirmação
    querent_email: "", // Campo adicional para email
    querent: {
      full_name: "",
      zodiac_sign: "",
      ascendant: "Não sei",
      moon_sign: "Não sei",
      pronoun: {
        she: false,
        he: false,
        other: "Não informado",
      },
    },
    channeled_person: {
      full_name: "",
      zodiac_sign: "Não sei",
      ascendant: "Não sei",
      moon_sign: "Não sei",
      pronoun: {
        she: false,
        he: false,
        other: "Não informado",
      },
    },
    relationship_status: {
      together: false,
      separated: false,
      romantic_interest: true,
    },
    contact_frequency: {
      daily: false,
      occasionally: true,
      rarely: false,
      no_contact: false,
    },
    communication: {
      open_honest: true,
      careful_subtle: false,
      unstable: false,
      no_emotional_talk: false,
    },
    recent_changes: {
      important_conversation: false,
      fight_distancing: false,
      special_moment: false,
      none: true,
    },
    letter_style: {
      direct_honest: false,
      gentle_hopeful: false,
      deep_emotional: false,
      trust_channeling: true,
    },
    desired_answers: "",
  };
}

// Keep old constant for backwards compatibility (though it won't be used)
const initialFormData: TarotRequestCreate = getInitialFormData("pt-BR");
const WIZARD_STEP_ORDER = [2, 3, 7, 1, 8] as const;

function getPrefilledFormData(
  language: string,
  contacts: ContactHistory,
  personal: PersonalHistory,
  t: (key: string, fallback?: string) => string,
): TarotRequestCreate {
  const initial = getInitialFormData(language);
  return {
    ...initial,
    delivery_option: language === "en-US"
      ? "email"
      : contacts.deliveryOption ?? initial.delivery_option,
    querent_phone: contacts.phones[0] || "",
    querent_email: contacts.emails[0] || "",
    querent: {
      ...initial.querent,
      full_name: personal.names[0] || "",
      zodiac_sign: getZodiacSign(personal.birthDates[0] || "", t),
      ...(personal.birthDates[0] ? { birth_date: personal.birthDates[0] } : {}),
    },
    channeled_person: {
      ...initial.channeled_person,
      full_name: personal.channeledNames[0] || "",
    },
  };
}

function buildDesiredAnswers(
  situationContext: string,
  mainDoubts: string,
  doubtsHeading: string,
) {
  const context = situationContext.trim();
  const doubts = mainDoubts.trim();

  if (!context && !doubts) return "";
  if (!doubts) return context;

  const doubtsSection = `${doubtsHeading}\n\n${doubts}`;
  return context ? `${context}\n\n${doubtsSection}` : doubtsSection;
}

export default function TarotRequestFormCompact({
  autoStart = false,
}: { autoStart?: boolean } = {}) {
  const { t } = useI18n();
  const router = useRouter();
  const [formData, setFormData] = useState<TarotRequestCreate>(initialFormData);
  const [currentStep, setCurrentStep] = useState<number>(WIZARD_STEP_ORDER[0]);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [previousAutoStart, setPreviousAutoStart] = useState(autoStart);
  const clientReady = useClientReady();
  const [initializedLanguage, setInitializedLanguage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set()); // Estado para campos com erro
  const [tarotRequestId, setTarotRequestId] = useState<number | null>(null); // ID da request criada
  const [showSuccessScreen, setShowSuccessScreen] = useState(false); // Estado para tela de sucesso
  const [step7SituationContext, setStep7SituationContext] = useState("");
  const [step7MainDoubts, setStep7MainDoubts] = useState("");
  const [showDoubtsDialog, setShowDoubtsDialog] = useState(false);
  const doubtsDialogOpenedAtRef = useRef(0);
  const [emailDomainSuggestionReviewedFor, setEmailDomainSuggestionReviewedFor] =
    useState<string | null>(null);
  const [blockedEmailDomainFor, setBlockedEmailDomainFor] = useState<string | null>(
    null,
  );
  const [recentContacts, setRecentContacts] = useState<ContactHistory>(emptyContactHistory);
  const [contactToConfirm, setContactToConfirm] = useState<{ email?: string; phone?: string } | null>(null);
  const contactDialogRef = useRef<HTMLDivElement>(null);
  const confirmContactButtonRef = useRef<HTMLButtonElement>(null);
  const [recentPersonalDetails, setRecentPersonalDetails] = useState<PersonalHistory>(emptyPersonalHistory);
  const [successData, setSuccessData] = useState<{
    requestId: string;
    whatsappNumber: string;
    querentName: string;
    valueBRL?: number;
    currency?: string; // Moeda do pagamento (BRL, USD, etc)
    priorityDelivery?: boolean;
    priorityDeliveryFeeCents?: number;
    deliveryTargetMinutes?: number;
    loveMap?: SuccessLoveMap | null;
  } | null>(null); // Dados para tela de sucesso
  const [pricingConfig, setPricingConfig] = useState<PricingConfig | null>(
    null,
  ); // Configuração de preços

  // Keep the latest created request id synchronously to avoid state race on success callback
  const latestRequestIdRef = useRef<number | null>(null);
  const latestCreatedRequestRef = useRef<{
    is_priority_delivery?: boolean;
    priority_delivery_fee_cents?: number;
    delivery_target_minutes?: number;
    love_map?: LoveMapResponse | null;
    stripe_amount_cents?: number;
    hotmart_amount_cents?: number;
    stripe_currency?: string;
    mp_amount_cents?: number;
    mp_currency?: string;
  } | null>(null);
  const trackedWizardStepsRef = useRef<Set<number>>(new Set());
  const getWizardStepNumber = (step: number): number => {
    const index = WIZARD_STEP_ORDER.indexOf(
      step as (typeof WIZARD_STEP_ORDER)[number],
    );
    return index >= 0 ? index + 1 : 1;
  };
  const getAnalyticsStepLabel = (step: number): string => {
    const labels: Record<number, string> = {
      1: "Contato",
      2: "Sobre Você",
      3: "Pessoa Canalizada",
      7: "Contexto e Pergunta",
      8: "Pagamento",
    };
    return labels[step] || `Step ${step}`;
  };

  // State management

  // Se solicitado, inicia diretamente no formulário (Step1)
  if (autoStart !== previousAutoStart) {
    setPreviousAutoStart(autoStart);
    if (autoStart) {
      setCurrentStep(WIZARD_STEP_ORDER[0]);
    }
  }

  useEffect(() => {
    if (!contactToConfirm) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    confirmContactButtonRef.current?.focus();

    const handleDialogKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setContactToConfirm(null);
      } else if (event.key === "Tab") {
        const buttons = contactDialogRef.current?.querySelectorAll<HTMLButtonElement>("button");
        if (!buttons?.length) return;
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleDialogKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDialogKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [contactToConfirm]);

  // Reset form data when language changes (ensures delivery_option matches language)
  if (clientReady && initializedLanguage !== config.i18n.language) {
    setInitializedLanguage(config.i18n.language);
    const contacts = readContactHistory();
    const personal = readPersonalHistory();
    setRecentContacts(contacts);
    setRecentPersonalDetails(personal);
    setEmailDomainSuggestionReviewedFor(contacts.emails[0] || null);
    setFormData(getPrefilledFormData(config.i18n.language, contacts, personal, t));
    setStep7SituationContext("");
    setStep7MainDoubts("");
  }

  // Load pricing configuration on component mount
  useEffect(() => {
    const loadPricing = async () => {
      try {
        const pricing = await pricingService.getPricingConfig();
        setPricingConfig(pricing);
        console.log("[Pricing] Configuração carregada:", pricing);
      } catch (error) {
        console.error("[Pricing] Erro ao carregar configuração:", error);
        // Use fallback configuration
        setPricingConfig({
          price_cents: config.pricing.fallback.priceCents,
          price_display: config.pricing.fallback.priceDisplay,
          currency: config.pricing.fallback.currency,
          description: config.pricing.fallback.description,
        });
      }
    };

    loadPricing();
  }, []);

  // Track step progress when entering each step
  useEffect(() => {
    if (isAuthenticating || authError) return;

    const email = formData.querent_email || undefined;
    const phone = formData.querent_phone || undefined;
    const externalId = tarotRequestId ? String(tarotRequestId) : undefined;
    const wizardStepNumber = getWizardStepNumber(currentStep);
    if (trackedWizardStepsRef.current.has(wizardStepNumber)) return;
    trackedWizardStepsRef.current.add(wizardStepNumber);

    void trackStep(wizardStepNumber, {
      label: getAnalyticsStepLabel(currentStep),
      email,
      phone,
      externalId,
      maxSteps: WIZARD_STEP_ORDER.length,
      alsoPixel: false,
      sendInternal: true,
      sendMeta: false,
    });
  }, [currentStep, isAuthenticating, authError, formData.querent_email, formData.querent_phone, tarotRequestId]);

  useEffect(() => {
    const authenticate = async () => {
      console.log("[AutoLogin] Iniciando autenticação automática...");
      try {
        const success = await TarotApiService.autoLogin();
        console.log("[AutoLogin] Resultado do autoLogin:", success);
        if (!success) {
          console.warn("[AutoLogin] Falha na autenticação automática.");
          setAuthError(
            "Erro na autenticação automática. Tente recarregar a página.",
          );
        } else {
          console.log(
            "[AutoLogin] Autenticação automática realizada com sucesso!",
          );
        }
      } catch (error: unknown) {
        console.error("[AutoLogin] Erro de conexão com o servidor:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Erro desconhecido";
        setAuthError("Erro de conexão com o servidor: " + errorMessage);
      } finally {
        setIsAuthenticating(false);
        console.log(
          "[AutoLogin] Finalizou tentativa de autenticação automática.",
        );
      }
    };

    authenticate();
  }, []);

  // Auto-scroll to top when payment success screen is shown
  useEffect(() => {
    if (showSuccessScreen) {
      window.scrollTo(0, 0);
    }
  }, [showSuccessScreen]);

  // Track step changes and handle payment step logic
  useEffect(() => {
    // InitiateCheckout será disparado quando o orderId estiver disponível (onRequestIdReady)
  }, []);

  // Force token renewal when reaching payment step (step 8)
  useEffect(() => {
    if (currentStep === 8) {
      const renewTokenForPayment = async () => {
        console.log(
          "[Payment] Usuário chegou ao step de pagamento, renovando token...",
        );
        try {
          const success = await TarotApiService.forceTokenRenewal();
          if (success) {
            console.log(
              "[Payment] Token renovado com sucesso para o pagamento",
            );
          } else {
            console.warn(
              "[Payment] Falha ao renovar token - usuário pode ter problemas no pagamento",
            );
            setMessage({
              type: "error",
              text: t(
                "step8.error.auth",
                "Erro de autenticação. Por favor, recarregue a página.",
              ),
            });
          }
        } catch (error) {
          console.error("[Payment] Erro ao renovar token:", error);
          setMessage({
            type: "error",
            text: t(
              "step8.error.connection",
              "Erro de conexão. Por favor, verifique sua internet e tente novamente.",
            ),
          });
        }
      };

      renewTokenForPayment();
    }
  }, [
    currentStep,
    formData.querent_email,
    formData.querent_phone,
    tarotRequestId,
    t,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (field: string, value: any) => {
    if (field === "querent_email" || field === "querent_phone" || field === "delivery_option") {
      setContactToConfirm(null);
    }
    if (field === "querent_email") {
      setEmailDomainSuggestionReviewedFor(null);
      setBlockedEmailDomainFor(null);
    }

    // Persistido fora do updater do setFormData, que precisa ser puro: o React
    // pode reexecutar o updater, e sessionStorage é I/O síncrono.
    if (field === "querent_email" || field === "querent_phone") {
      try {
        if (value && typeof window !== "undefined") {
          const storageKey =
            field === "querent_email" ? "user_email" : "user_phone";
          sessionStorage.setItem(storageKey, String(value));
        }
      } catch {
        // Ignore storage errors
      }
    }

    if (field === "delivery_option" && value === "whatsapp") {
      setEmailDomainSuggestionReviewedFor(null);
      setBlockedEmailDomainFor(null);
      setFieldErrors((prev) => {
        const next = new Set(prev);
        next.delete("querent_email");
        return next;
      });
      try {
        sessionStorage.removeItem("user_email");
      } catch {
        // Storage can be unavailable in private browsing.
      }
    }

    setFormData((prev) => {
      if (field.includes(".")) {
        const [parent, child, grandchild] = field.split(".");
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const parentObj = prev[parent as keyof TarotRequestCreate] as any;

        return {
          ...prev,
          [parent]: {
            ...parentObj,
            ...(grandchild
              ? {
                  [child]: {
                    ...parentObj[child],
                    [grandchild]: value,
                  },
                }
              : {
                  [child]: value,
                }),
          },
        } as TarotRequestCreate;
      }

      return {
        ...prev,
        [field]: value,
        ...(field === "delivery_option" && value === "whatsapp"
          ? { querent_email: "" }
          : {}),
      } as TarotRequestCreate;
    });
  };

  const validateEmailDomainOnAdvance = (): boolean | Promise<boolean> => {
    if (currentStep !== 1) return true;

    if (!formData.delivery_option || formData.delivery_option === "whatsapp") return true;

    const emailValue = (formData.querent_email || "").trim();
    if (!emailValue) return true;

    const emailDomainSuggestion = detectLikelyEmailDomainTypo(emailValue);
    const reviewedEmail = (emailDomainSuggestionReviewedFor || "")
      .trim()
      .toLowerCase();

    if (
      emailDomainSuggestion &&
      reviewedEmail !== emailValue.toLowerCase()
    ) {
      setFieldErrors((prev) => {
        const next = new Set(prev);
        next.add("querent_email");
        return next;
      });
      setMessage({
        type: "error",
        text: t(
          "step1.validation.email_domain_review",
          "Revise o domínio do email antes de continuar.",
        ),
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) return true;

    const checkBlockedEmailDomain = TarotApiService.checkBlockedEmailDomain;
    if (typeof checkBlockedEmailDomain !== "function") return true;

    return checkBlockedEmailDomain(emailValue)
      .then((result) => {
        if (!result.blocked) {
          setBlockedEmailDomainFor(null);
          return true;
        }

        const normalizedEmail = emailValue.toLowerCase();
        setBlockedEmailDomainFor(normalizedEmail);
        setFieldErrors((prev) => new Set(prev).add("querent_email"));
        setMessage({
          type: "error",
          text: t(
            "step1.email_domain_blocked",
            "Este domínio de email não existe. Revise o endereço para continuar.",
          ),
        });
        return false;
      })
      .catch((error) => {
        // The local format and typo checks still apply. Do not prevent checkout
        // when a transient API failure stops the domain lookup.
        console.warn("[Email] Could not check blocked domain:", error);
        return true;
      });

  };

  const syncDesiredAnswers = (
    nextSituationContext: string,
    nextMainDoubts: string,
  ) => {
    const desiredAnswers = buildDesiredAnswers(
      nextSituationContext,
      nextMainDoubts,
      t("step7.doubts.heading", "Minhas dúvidas:"),
    );
    handleInputChange("desired_answers", desiredAnswers || null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("handleSubmit chamado, currentStep:", currentStep);

    // No Step 8 o pagamento é feito diretamente no componente de Mercado Pago (Bricks)
    if (currentStep === 8) return;

    console.log("Não é Step 8, validando e avançando...");
    // Para outros steps, validar e avançar
    await nextStep();
  };

  // Fluxo de pagamento via Mercado Pago é tratado no Step8Payment (Bricks)

  const handleBackToLanding = () => {
    // Agora o botão "Voltar" leva para a Home
    router.push("/");
  };

  // Função de validação para cada step
  const validateCurrentStep = () => {
    const errors = new Set<string>();
    let isValid = true;
    let errorMessage = "";

    switch (currentStep) {
      case 1:
        // Step 1: Validação do canal de entrega
        const isWhatsAppAvailable = isPortugueseLanguage();
        if (!formData.delivery_option) {
          errors.add("delivery_option");
          errorMessage = t(
            "step1.validation.delivery_required",
            "Selecione ao menos um canal de entrega.",
          );
          isValid = false;
        }
        const wantsWhatsapp = formData.delivery_option === "whatsapp" || formData.delivery_option === "email_whatsapp";
        const wantsEmail = formData.delivery_option === "email" || formData.delivery_option === "email_whatsapp";

        // Somente validar WhatsApp se estiver disponível para o idioma e o usuário escolheu essa opção
        if (wantsWhatsapp && isWhatsAppAvailable) {
          if (!formData.querent_phone) {
            errors.add("querent_phone");
            isValid = false;
          }

          if (!isValid) {
            errorMessage = "Por favor, preencha o número do WhatsApp.";
          } else {
            // Validar se o número é válido para WhatsApp
            const phoneValidation = validatePhoneForWhatsApp(
              formData.querent_phone,
              getMarketPhoneCountry(),
            );

            if (!phoneValidation.isValid) {
              errors.add("querent_phone");
              errorMessage = `Número principal: ${phoneValidation.errorMessage}`;
              isValid = false;
            }
          }
        }

        // Email é obrigatório apenas quando selecionado como canal de entrega.
        const emailValue = (formData.querent_email || "").trim();
        if (wantsEmail && !emailValue) {
          errors.add("querent_email");
          const emailRequiredMsg = t(
            "step1.validation.email_required",
            "Informe seu email para receber a carta por esse canal.",
          );
          errorMessage = errorMessage
            ? `${errorMessage} ${t(
                "step1.validation.also",
                "Além disso",
              )}, ${emailRequiredMsg.toLowerCase()}`
            : emailRequiredMsg;
          isValid = false;
        } else if (wantsEmail) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(emailValue)) {
            errors.add("querent_email");
            const emailInvalidMsg = t(
              "step1.validation.email_invalid",
              "O email informado é inválido.",
            );
            errorMessage = errorMessage
              ? `${errorMessage} ${t(
                  "step1.validation.also",
                  "Além disso",
                )}, ${emailInvalidMsg.toLowerCase()}`
              : emailInvalidMsg;
            isValid = false;
          }
          const emailDomainSuggestion = detectLikelyEmailDomainTypo(emailValue);
          const reviewedEmail = (emailDomainSuggestionReviewedFor || "")
            .trim()
            .toLowerCase();
          if (
            emailDomainSuggestion &&
            reviewedEmail !== emailValue.toLowerCase()
          ) {
            errors.add("querent_email");
            const emailReviewMsg = t(
              "step1.validation.email_domain_review",
              "Revise o domínio do email antes de continuar.",
            );
            errorMessage = errorMessage
              ? `${errorMessage} ${t(
                  "step1.validation.also",
                  "Além disso",
                )}, ${emailReviewMsg.toLowerCase()}`
              : emailReviewMsg;
            isValid = false;
          }
        }

        break;

      case 2:
        if (!formData.querent.full_name.trim()) {
          errors.add("querent.full_name");
          errorMessage = "Por favor, preencha seu nome.";
          isValid = false;
        } else if (
          !(
            formData.querent as unknown as { birth_date?: string }
          ).birth_date?.trim()
        ) {
          errors.add("querent.birth_date");
          errorMessage = "Por favor, preencha sua data de nascimento.";
          isValid = false;
        } else if (!(formData.querent.zodiac_sign || "").trim()) {
          errors.add("querent.zodiac_sign");
          errorMessage = "Por favor, preencha uma data de nascimento válida.";
          isValid = false;
        }
        break;
        // Step 2: Validação das informações do consulente
        if (!formData.querent.full_name.trim()) {
          errors.add("querent.full_name");
          errorMessage = "Por favor, preencha seu nome completo.";
          isValid = false;
        } else if (
          !(
            formData.querent as unknown as { birth_date?: string }
          ).birth_date?.trim()
        ) {
          errors.add("querent.birth_date");
          errorMessage = "Por favor, preencha sua data de nascimento.";
          isValid = false;
        } else if (!(formData.querent.zodiac_sign || "").trim()) {
          errors.add("querent.zodiac_sign");
          errorMessage = "Por favor, preencha seu signo.";
          isValid = false;
        }
        break;

      case 3:
        // Step 3: Validação das informações da pessoa canalizada
        if (!formData.channeled_person.full_name.trim()) {
          errors.add("channeled_person.full_name");
          errorMessage = "Por favor, preencha o nome completo da pessoa.";
          isValid = false;
        }
        break;

      case 4:
        if (
          !(
            formData.querent as unknown as { birth_date?: string }
          ).birth_date?.trim()
        ) {
          errors.add("querent.birth_date");
          errorMessage = "Por favor, preencha sua data de nascimento.";
          isValid = false;
        } else if (!(formData.querent.zodiac_sign || "").trim()) {
          errors.add("querent.zodiac_sign");
          errorMessage = "Por favor, preencha uma data de nascimento válida.";
          isValid = false;
        }
        break;
        // Step 4: Validação do status do relacionamento
        if (
          !formData.relationship_status.together &&
          !formData.relationship_status.separated &&
          !formData.relationship_status.romantic_interest
        ) {
          errors.add("relationship_status");
          errorMessage =
            "Por favor, selecione o status atual do relacionamento.";
          isValid = false;
        }
        break;

      case 5:
        // Step 5 removed from UI. Keep default communication as open_honest.
        if (!formData.communication.open_honest) {
          handleInputChange("communication", {
            open_honest: true,
            careful_subtle: false,
            unstable: false,
            no_emotional_talk: false,
          });
        }
        break;

      case 6:
        // Step 6: Validação das mudanças recentes - SKIPPED
        // Step 6 is now hidden, recent_changes.none is set to true by default
        // No validation needed
        break;

      case 7:
        console.log("[validateCurrentStep] Validando Step 7");
        console.log("letter_style:", formData.letter_style);
        console.log("desired_answers:", formData.desired_answers);

        if (!step7SituationContext.trim()) {
          errors.add("step7_context");
          errorMessage = "Por favor, informe um breve contexto da situação.";
          isValid = false;
        }
        if (!step7MainDoubts.trim()) {
          errors.add("step7_main_doubts");
          errorMessage = errorMessage
            ? `${errorMessage} Além disso, informe suas 3 principais dúvidas.`
            : "Por favor, informe suas 3 principais dúvidas.";
          isValid = false;
          console.log("[validateCurrentStep] Erro: dúvidas principais vazias");
        }
        if (step7SituationContext.trim() && step7MainDoubts.trim()) {
          console.log("[validateCurrentStep] Step 7 válido!");
        }
        break;

      case 8:
        // Step 8: Página de pagamento - sem validação adicional
        break;
    }

    setFieldErrors(errors);

    if (!isValid) {
      setMessage({
        type: "error",
        text: errorMessage,
      });
      return false;
    }

    return true;
  };

  const reportDoubtsPopup = (action: "displayed" | "used") => {
    void ErrorLogService.logError(new Error(`[Step7 doubts popup] ${action}`), {
      component: "Step7DoubtsDialog",
      severity: ErrorSeverity.INFO,
      extra_data: {
        type: "step7_doubts_popup",
        action,
        context_length: step7SituationContext.trim().length,
        doubts_length: step7MainDoubts.trim().length,
        ...(action === "used" ? { elapsed_ms: Date.now() - doubtsDialogOpenedAtRef.current } : {}),
      },
    }).catch(() => {});
  };

  const nextStep = async () => {
    if (currentStep === 7 && step7SituationContext.trim() && !step7MainDoubts.trim()) {
      if (!showDoubtsDialog) {
        doubtsDialogOpenedAtRef.current = Date.now();
        setShowDoubtsDialog(true);
        setMessage(null);
        reportDoubtsPopup("displayed");
      }
      return;
    }
    const currentIndex = WIZARD_STEP_ORDER.indexOf(
      currentStep as (typeof WIZARD_STEP_ORDER)[number],
    );
    console.log(
      `[nextStep] Tentando avançar do step ${currentStep} para ${
        currentStep + 1
      }`,
    );

    // Validar o step atual antes de avançar
    const domainValidation = validateEmailDomainOnAdvance();
    const emailDomainValid =
      typeof domainValidation === "boolean"
        ? domainValidation
        : await domainValidation;
    if (!emailDomainValid) {
      console.log(`[nextStep] Validação de domínio de email falhou no step ${currentStep}`);
      return;
    }
    if (!validateCurrentStep()) {
      console.log(`[nextStep] Validação falhou no step ${currentStep}`);
      return;
    }

    if (currentStep === 2) {
      const name = formData.querent.full_name.trim();
      const birthDate = ((formData.querent as { birth_date?: string }).birth_date || "").trim();
      setRecentPersonalDetails(rememberPersonalDetails(recentPersonalDetails, { name, birthDate }));
    }
    if (currentStep === 3) {
      const channeledName = formData.channeled_person.full_name.trim();
      setRecentPersonalDetails(rememberPersonalDetails(recentPersonalDetails, { channeledName }));
    }

    if (currentStep === 1) {
      const wantsEmail = formData.delivery_option === "email" || formData.delivery_option === "email_whatsapp";
      const wantsPhone = formData.delivery_option === "whatsapp" || formData.delivery_option === "email_whatsapp";
      const email = wantsEmail ? (formData.querent_email || "").trim().toLowerCase() : undefined;
      const phone = wantsPhone ? formData.querent_phone.trim() : undefined;
      if ((email && !recentContacts.emails.includes(email)) || (phone && !recentContacts.phones.includes(phone))) {
        setContactToConfirm({ email, phone });
        return;
      }
      setRecentContacts(rememberContacts(recentContacts, email, phone, formData.delivery_option || undefined));
    }

    if (currentIndex >= 0 && currentIndex < WIZARD_STEP_ORDER.length - 1) {
      console.log(
        `[nextStep] Validação OK, avançando para step ${currentStep + 1}`,
      );

      // desired_answers é materializado só ao sair do Step 7. Sincronizar a cada
      // tecla re-renderizava o wizard inteiro e travava a digitação em webviews lentas.
      if (currentStep === 7) {
        syncDesiredAnswers(step7SituationContext, step7MainDoubts);
      }

      const nextStepNumber = WIZARD_STEP_ORDER[currentIndex + 1];
      setCurrentStep(nextStepNumber);
      setFieldErrors(new Set()); // Limpar erros ao avançar
      setMessage(null); // Limpar mensagens ao avançar

      window.scrollTo(0, 0);

      console.log(`[nextStep] Step atualizado para ${nextStepNumber}`);
    } else {
      console.log(`[nextStep] Já está no último step (${currentStep})`);
    }
  };

  const confirmContactAndAdvance = () => {
    if (!contactToConfirm) return;
    const wantsEmail = formData.delivery_option === "email" || formData.delivery_option === "email_whatsapp";
    const wantsPhone = formData.delivery_option === "whatsapp" || formData.delivery_option === "email_whatsapp";
    const email = wantsEmail ? (formData.querent_email || "").trim().toLowerCase() : undefined;
    const phone = wantsPhone ? formData.querent_phone.trim() : undefined;
    if (email !== contactToConfirm.email || phone !== contactToConfirm.phone) {
      setContactToConfirm(null);
      return;
    }
    setRecentContacts(rememberContacts(recentContacts, email, phone, formData.delivery_option || undefined));
    setContactToConfirm(null);
    setCurrentStep(8);
    setFieldErrors(new Set());
    setMessage(null);
    window.scrollTo(0, 0);
  };

  // Fluxo de confirmação é automático via webhook/status do Mercado Pago

  // Função para tratar erro de pagamento
  const handlePaymentError = (error: string) => {
    console.error("Erro no pagamento:", error);
    setMessage({
      type: "error",
      text: "Erro no pagamento: " + error,
    });
  };

  // Função para voltar ao início da aplicação (leva para a Home)
  const handleBackToStep1 = () => {
    const contacts = readContactHistory();
    const personal = readPersonalHistory();
    setRecentContacts(contacts);
    setRecentPersonalDetails(personal);
    setEmailDomainSuggestionReviewedFor(contacts.emails[0] || null);
    setFormData(getPrefilledFormData(config.i18n.language, contacts, personal, t));
    setStep7SituationContext("");
    setStep7MainDoubts("");
    setCurrentStep(WIZARD_STEP_ORDER[0]);
    trackedWizardStepsRef.current.clear();
    setShowSuccessScreen(false);

    router.push("/form");
  };

  const prevStep = () => {
    const currentIndex = WIZARD_STEP_ORDER.indexOf(
      currentStep as (typeof WIZARD_STEP_ORDER)[number],
    );
    if (currentIndex > 0) {
      const prevStepNumber = WIZARD_STEP_ORDER[currentIndex - 1];
      setCurrentStep(prevStepNumber);
      setFieldErrors(new Set()); // Limpar erros ao voltar
      setMessage(null); // Limpar mensagens ao voltar

      window.scrollTo(0, 0);
    }
  };

  // Loading state while authenticating
  const pageBackgroundStyle = {
    background: "#ffffff",
  };

  if (isAuthenticating) {
    return (
      <div
        className="order-flow-font flex min-h-screen items-center justify-center py-12"
        style={pageBackgroundStyle}
      >
        <div className="rounded-xl p-6 shadow-xl border border-tarot-dourado/20 text-center max-w-sm bg-white">
          <div className="text-3xl mb-3">🔮</div>
          <h2 className="text-lg font-title font-bold text-tarot-carmim mb-3">
            {t("form.loading.title", "Preparando sua experiência...")}
          </h2>
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-tarot-dourado rounded-full"></div>
            <div className="w-2 h-2 bg-tarot-carmim rounded-full"></div>
            <div className="w-2 h-2 bg-tarot-marrom rounded-full"></div>
          </div>
          <p className="text-tarot-marrom mt-3 text-sm">
            {t(
              "form.loading.subtitle",
              "Conectando com as energias místicas...",
            )}
          </p>
        </div>
      </div>
    );
  }

  // Authentication error state
  if (authError) {
    return (
      <div
        className="order-flow-font flex min-h-screen items-center justify-center py-12"
        style={pageBackgroundStyle}
      >
        <div className="rounded-xl p-6 shadow-xl border border-red-400/50 text-center max-w-sm bg-white">
          <div className="text-3xl mb-3">⚠️</div>
          <h2 className="text-lg font-title font-bold text-red-600 mb-3">
            {t("form.auth.error.title", "Erro de Conexão")}
          </h2>
          <p className="text-red-800 mb-4 text-sm">{authError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-tarot-carmim text-white px-4 py-2 rounded-lg hover:bg-tarot-marrom transition-colors text-sm"
          >
            {t("form.auth.error.retry", "🔄 Tentar Novamente")}
          </button>
        </div>
      </div>
    );
  }

  const stepTitles = [
    t("form.steps.1.title", "Para onde enviaremos sua carta exclusiva?"),
    t("form.steps.2.title", "Sua leitura começa aqui ✨"),
    t("form.steps.3.title", "Pessoa a ser Canalizada"),
    t("form.steps.5.title", "Estilo de Comunicação"),
    t("form.steps.6.title", "Mudanças Recentes"),
    t("form.steps.7.title", "Preferências da Carta"),
    t("form.steps.8.title", "💌 Confirme para receber sua carta personalizada"),
  ];

  const stepIcons = ["💌", "🙋‍♀️", "👤", "💕", "💬", "🔄", "📝", "💳"];
  const stepDescriptions = [
    t("form.steps.1.desc", ""),
    t(
      "form.steps.2.desc",
      "Só preciso de duas informações suas para personalizar sua leitura.",
    ),
    t("form.steps.3.desc", "Quem você deseja canalizar"),
    t("form.steps.5.desc", "Como vocês conversam sobre sentimentos"),
    t("form.steps.6.desc", "Eventos importantes recentes"),
    t("form.steps.7.desc", "Como você quer receber sua carta"),
    t("form.steps.8.desc", "Confirme os dados e efetue o pagamento"),
  ];
  void stepTitles;
  void stepIcons;
  void stepDescriptions;
  const displayedStepTitles = [
    t("form.steps.2.title", "Sua leitura começa aqui ✨"),
    t("form.steps.3.title", "Pessoa a ser Canalizada"),
    t("form.steps.7.title", "💌 Conte o que está acontecendo"),
    t("form.steps.1.title", "Para onde enviaremos sua carta exclusiva?"),
    t(
      "form.steps.payment_only.title",
      "Sua leitura está pronta para começar",
    ),
  ];
  const displayedStepIcons = ["🙋‍♀️", "👤", "📝", "📩", "✨"];
  const displayedStepDescriptions = [
    t(
      "form.steps.2.desc",
      "Só preciso de duas informações suas para personalizar sua leitura.",
    ),
    t("form.steps.3.desc", "Quem você deseja canalizar"),
    t("form.steps.7.desc", ""),
    t("form.steps.1.desc", ""),
    t(
      "form.steps.payment_only.desc",
      "Falta apenas um passo para receber tudo o que ele sente, mas nunca disse.",
    ),
  ];
  const currentWizardIndex = WIZARD_STEP_ORDER.indexOf(
    currentStep as (typeof WIZARD_STEP_ORDER)[number],
  );
  const totalWizardSteps = WIZARD_STEP_ORDER.length;
  const currentWizardStep = Math.max(1, currentWizardIndex + 1);
  const progressPercent = Math.min(
    100,
    Math.max(0, Math.round((currentWizardStep / totalWizardSteps) * 100)),
  );
  const shouldShowProgress = currentStep !== 8;

  // Tela de sucesso do pagamento
  if (showSuccessScreen && successData) {
    return (
      <PaymentSuccessScreen
        requestId={successData.requestId}
        whatsappNumber={successData.whatsappNumber}
        querentName={successData.querentName}
        valueBRL={successData.valueBRL}
        currency={successData.currency}
        priorityDelivery={successData.priorityDelivery}
        priorityDeliveryFeeCents={successData.priorityDeliveryFeeCents}
        deliveryTargetMinutes={successData.deliveryTargetMinutes}
        loveMap={successData.loveMap}
        email={formData.delivery_option === "whatsapp" ? undefined : formData.querent_email || undefined}
        deliveryOption={formData.delivery_option || "email"}
        onBackToStep1={handleBackToStep1}
      />
    );
  }

  return (
    <div className="order-flow-font min-h-screen py-3" style={pageBackgroundStyle}>
      <div className="container mx-auto px-4 py-3 max-w-4xl">
        {/* Container principal com borda dourada ornamental */}
        <div
          className="rounded-2xl p-4 md:p-6 relative shadow-xl"
          style={{
            backgroundColor: "#fdf2f2",
            border: "2px solid #800000",
            boxShadow:
              "0 8px 25px rgba(128,0,0,0.3), 0 0 0 1px rgba(128,0,0,0.1)",
          }}
        >
          {/* Header do formulário com botão voltar + título dinâmico do step */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-between mb-4">
              {false ? (
                <button
                  onClick={handleBackToLanding}
                  className="flex items-center space-x-2 px-4 py-2 text-sm rounded-lg transition-colors font-subtitle border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 shadow-none"
                >
                  <span className="text-lg">←</span>
                  <span>{t("common.back", "Voltar")}</span>
                </button>
              ) : (
                <div></div>
              )}
              <div className="flex flex-col items-center text-center">
                <div className="text-2xl md:text-3xl drop-shadow-lg">
                  {displayedStepIcons[currentWizardIndex]}
                </div>
                <h2
                  className="text-lg md:text-xl font-bold tracking-wider font-title"
                  style={{
                    color: "#800000",
                    textShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  }}
                >
                  {displayedStepTitles[currentWizardIndex]}
                </h2>
                <p
                  className="text-sm md:text-base font-bold mt-1 font-subtitle"
                  style={{ color: "#4a2c2c" }}
                >
                  {displayedStepDescriptions[currentWizardIndex]}
                </p>
                {shouldShowProgress && (
                  <div className="w-full max-w-xs mt-3">
                    <div
                      className="h-2 rounded-full overflow-hidden"
                      style={{ backgroundColor: "rgba(128, 0, 0, 0.15)" }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${progressPercent}%`,
                          background:
                            "linear-gradient(to right, #059669, #10b981, #34d399)",
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div></div> {/* Spacer para centralizar */}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Form Content - direto, com espaçamento reduzido */}
            <div className="px-1 md:px-1 py-0">
              {/* Step 1: Contact Information */}
              {currentStep === 1 && (
                <ErrorBoundary componentName="Step1Contact">
                  <Step1Contact
                    formData={formData}
                    fieldErrors={fieldErrors}
                    onInputChange={handleInputChange}
                    emailDomainBlocked={
                      blockedEmailDomainFor ===
                      (formData.querent_email || "").trim().toLowerCase()
                    }
                    emailDomainSuggestionReviewedFor={
                      emailDomainSuggestionReviewedFor
                    }
                    onEmailDomainSuggestionReviewed={(email: string) => {
                      setEmailDomainSuggestionReviewedFor(
                        email.trim().toLowerCase(),
                      );
                    }}
                    onFieldErrorRemove={(field: string) => {
                      const newErrors = new Set(fieldErrors);
                      newErrors.delete(field);
                      setFieldErrors(newErrors);
                    }}
                  />
                </ErrorBoundary>
              )}

              {/* Step 2: Querent Information */}
              {currentStep === 2 && (
                <ErrorBoundary componentName="Step2Querent">
                  <Step2Querent
                    formData={formData}
                    fieldErrors={fieldErrors}
                    onInputChange={handleInputChange}
                    onFieldErrorRemove={(field: string) => {
                      const newErrors = new Set(fieldErrors);
                      newErrors.delete(field);
                      setFieldErrors(newErrors);
                    }}
                  />
                </ErrorBoundary>
              )}

              {/* Step 3: Channeled Person Information */}
              {currentStep === 3 && (
                <ErrorBoundary componentName="Step3ChanneledPerson">
                  <Step3ChanneledPerson
                    formData={formData}
                    fieldErrors={fieldErrors}
                    onInputChange={handleInputChange}
                    onFieldErrorRemove={(field: string) => {
                      const newErrors = new Set(fieldErrors);
                      newErrors.delete(field);
                      setFieldErrors(newErrors);
                    }}
                  />
                </ErrorBoundary>
              )}

              {/* Step 5 removed: Communication defaults to open_honest */}

              {/* Step 6: Recent Changes - HIDDEN (default: none) */}
              {/* Step 6 is now skipped, recent_changes.none is set to true by default */}

              {/* Step 7: Letter Preferences */}
              {currentStep === 7 && (
                <ErrorBoundary componentName="Step7Preferences">
                  <Step7Preferences
                    formData={formData}
                    situationContext={step7SituationContext}
                    mainDoubts={step7MainDoubts}
                    fieldErrors={fieldErrors}
                    onSituationContextChange={setStep7SituationContext}
                    onMainDoubtsChange={setStep7MainDoubts}
                    onFieldErrorRemove={(field: string) => {
                      const newErrors = new Set(fieldErrors);
                      newErrors.delete(field);
                      setFieldErrors(newErrors);
                    }}
                  />
                </ErrorBoundary>
              )}

              {/* Step 8: Payment */}
              {currentStep === 8 && pricingConfig && (
                <ErrorBoundary componentName="Step8Payment">
                  <Step8Payment
                    formData={formData}
                    onRequestIdReady={(id) => {
                      latestRequestIdRef.current = id;
                      setTarotRequestId(id);

                      console.info(
                        "[Analytics] Captured tarotRequestId from Step8Payment",
                        { id },
                      );
                      // Disparar InitiateCheckout APENAS quando tivermos o orderId
                      try {
                        trackInitiateCheckout({
                          orderId: id,
                          valueBRL: pricingConfig?.price_cents
                            ? pricingConfig.price_cents / 100
                            : undefined,
                          currency: pricingConfig?.currency || "BRL",
                          email: formData.querent_email,
                          phone:
                            formData.delivery_option !== "email"
                              ? formData.querent_phone
                              : undefined,
                          alsoPixel: true,
                        }).catch(() => {});
                      } catch {}
                    }}
                    onRequestCreated={(request) => {
                      latestCreatedRequestRef.current = request;
                    }}
                    onMercadoPagoSuccess={() => {
                      const stableRequestId =
                        latestRequestIdRef.current ?? tarotRequestId;
                      const createdRequest = latestCreatedRequestRef.current;
                      const totalAmountCents =
                        createdRequest?.stripe_amount_cents ??
                        createdRequest?.mp_amount_cents ??
                        createdRequest?.hotmart_amount_cents;
                      // Prepare success data from current form/pricing state
                      setSuccessData((prev) => ({
                        requestId: stableRequestId
                          ? String(stableRequestId)
                          : prev?.requestId || "",
                        whatsappNumber:
                          formData.delivery_option !== "email"
                            ? formData.querent_phone
                            : "",
                        querentName: formData.querent.full_name || "Cliente",
                        valueBRL: totalAmountCents
                          ? totalAmountCents / 100
                          : pricingConfig?.price_cents
                            ? pricingConfig.price_cents / 100
                          : undefined,
                        currency:
                          createdRequest?.stripe_currency ||
                          createdRequest?.mp_currency ||
                          pricingConfig?.currency ||
                          "BRL",
                        priorityDelivery:
                          createdRequest?.is_priority_delivery ?? false,
                        priorityDeliveryFeeCents:
                          createdRequest?.priority_delivery_fee_cents ?? 0,
                        deliveryTargetMinutes:
                          createdRequest?.delivery_target_minutes,
                        loveMap: toSuccessLoveMap(createdRequest?.love_map),
                      }));
                      setShowSuccessScreen(true);
                    }}
                    onPaymentError={handlePaymentError}
                    pricingConfig={pricingConfig}
                  />
                </ErrorBoundary>
              )}
            </div>

            {currentStep === 7 && showDoubtsDialog && (
              <Step7DoubtsDialog
                value={step7MainDoubts}
                onChange={setStep7MainDoubts}
                onClose={() => setShowDoubtsDialog(false)}
                onContinue={() => {
                  if (!step7SituationContext.trim() || !step7MainDoubts.trim()) return;
                  reportDoubtsPopup("used");
                  setShowDoubtsDialog(false);
                  void nextStep();
                }}
              />
            )}

            {currentStep === 1 && contactToConfirm && typeof document !== "undefined" && createPortal(
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                <div
                  ref={contactDialogRef}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="contact-confirmation-title"
                  aria-describedby="contact-confirmation-description"
                  className="w-full max-w-md rounded-2xl border border-[#800000] bg-[#fdf2f2] p-5 text-slate-800 shadow-2xl sm:p-6"
                >
                  <h3 id="contact-confirmation-title" className="font-title text-xl font-bold text-[#800000]">
                    {t("step1.confirm_contact.title", "Confira seus contatos")}
                  </h3>
                  <p id="contact-confirmation-description" className="mt-2 text-sm">
                    {t("step1.confirm_contact.description", "Estão corretos? Sua carta será enviada para os contatos abaixo.")}
                  </p>
                  <div className="mt-4 rounded-lg border border-[rgba(128,0,0,0.2)] bg-white p-3 text-sm">
                    {contactToConfirm.email && <p className="break-all"><strong>Email:</strong> {contactToConfirm.email}</p>}
                    {contactToConfirm.phone && <p className="break-all"><strong>WhatsApp:</strong> {contactToConfirm.phone}</p>}
                  </div>
                  <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                    <button ref={confirmContactButtonRef} type="button" onClick={confirmContactAndAdvance} className="rounded-lg bg-emerald-700 px-4 py-2 font-bold text-white hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                      {t("step1.confirm_contact.correct", "Sim, estão corretos")}
                    </button>
                    <button type="button" onClick={() => setContactToConfirm(null)} className="rounded-lg border border-slate-400 bg-white px-4 py-2 font-bold hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500">
                      {t("step1.confirm_contact.edit", "Quero ajustar")}
                    </button>
                  </div>
                </div>
              </div>,
              document.body,
            )}

            {/* Navigation buttons - limpo, sem fundo/borda */}
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={prevStep}
                className="px-4 py-2 text-sm rounded-lg transition-all duration-300 font-bold shadow-lg font-subtitle"
                style={{
                  visibility: currentWizardIndex === 0 ? "hidden" : "visible",
                  background:
                    "linear-gradient(to bottom, #059669, #047857, #065f46)",
                  border: "2px solid #34d399",
                  color: "#ffffff",
                  textShadow: "1px 1px 2px rgba(0,0,0,0.4)",
                  boxShadow: "0 6px 14px rgba(16, 185, 129, 0.24)",
                  cursor: currentWizardIndex === 0 ? "default" : "pointer",
                }}
              >
                {t("common.previous", "← Anterior")}
              </button>

              {currentWizardIndex < WIZARD_STEP_ORDER.length - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-3 text-base rounded-lg font-bold transition-all duration-300 shadow-lg font-title"
                  style={{
                    background:
                      "linear-gradient(to bottom, #059669, #047857, #065f46)",
                    border: "2px solid #34d399",
                    color: "#ffffff",
                    textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                    boxShadow: "0 6px 14px rgba(16, 185, 129, 0.24)",
                  }}
                >
                  <span className="drop-shadow-lg">
                    {currentStep === 1
                      ? t("form.cta.to_payment", "Ir para pagamento")
                      : currentStep === 2
                        ? t("form.cta.start", "Continuar →")
                        : t("form.cta.next", "Continuar →")}
                  </span>
                </button>
              ) : null}
            </div>
          </form>
        </div>

        {/* Status message - só exibe fora do step 8 */}
        {message && currentStep !== 8 && (
          <div
            className={`mt-6 p-5 rounded-lg ${
              message.type === "success"
                ? "bg-green-100 border border-green-400 text-green-700"
                : "bg-red-100 border border-red-400 text-red-700"
            }`}
          >
            <p className="text-center font-medium text-base">{message.text}</p>
          </div>
        )}
      </div>
    </div>
  );
}
